import React from 'react'
import { PropTypes } from 'prop-types'
import { Link } from 'react-router-dom'
import { IconContext } from 'react-icons'
import * as Ionicons from 'react-icons/io'
import AnimatedLogo from '@wellbeingxltd/fe-lib/images/dist/logo/AnimatedLogo.gif'
import './style.scss'

const Button = ({
    active,
    ariaDisabled,
    ariaLabel,
    children,
    className,
    cta,
    disabled,
    icon,
    link,
    linkExternal,
    loading = false,
    onClick,
    role,
    to = '',
    type = 'button',
    variant,
    ...rest
}) => {
    const variantToStyleParser = () => {
        let variantClassName = ''
        let variantClassNameModifier = ''
        let finalStyle = ''
        if (variant) {
            switch (variant.name) {
                case 'white':
                    variantClassName = 'button--white'
                    break
                case 'whiteBorder':
                    variantClassName = 'button--whiteBorder'
                    break
                case 'grey':
                    variantClassName = 'button--grey'
                    break
                case 'red':
                    variantClassName = 'button--red'
                    break
                case 'beigeBorder':
                    variantClassName = 'button--beigeBorder'
                    break
                case 'blueBorder':
                    variantClassName = 'button--blueBorder'
                    break
                case 'resourceBookingAccessButton':
                    variantClassName = variant.name
                    break
                case 'favouriteButton':
                    variantClassName = variant.name
                    variantClassNameModifier = variant.condition ? 'favouriteButton--favourited' : ''
                    break
                case 'bluePill': // the story ends, you wake up in your bed and believe whatever you want to believe
                    variantClassName = 'button--bluePill'
                    break
                case 'thumbUP':
                    variantClassName = 'button--thumbUp'
                    variantClassNameModifier = variant.condition ? 'favouriteButton--favourited' : ''
                    break
                case 'thumbDOWN':
                    variantClassName = 'button--thumbDown'
                    variantClassNameModifier = variant.condition ? 'favouriteButton--favourited' : ''
                    break
                case 'Sleep':
                    variantClassName = 'button--Sleep'
                    break
                case 'Exercise':
                    variantClassName = 'button--Exercise'
                    break
                case 'Nutrition':
                    variantClassName = 'button--Nutrition'
                    break
                case 'MeaningfulActivity':
                    variantClassName = 'button--MeaningfulActivity'
                    break
                case 'StressManagement':
                    variantClassName = 'button--StressManagement'
                    break
                case 'Community':
                    variantClassName = 'button--Community'
                    break
                default:
                    variantClassName = ''
                    variantClassNameModifier = ''
            }

            finalStyle = `${variantClassName} ${variantClassNameModifier}`
        }
        return finalStyle
    }

    // Interpolate the icon component because react-icons uses components rather than props for the icon name
    const iconComponent = icon ? React.createElement(Ionicons[icon]) : <></>

    const buttonChildren = (
        <>
            {loading ? (
                <img src={AnimatedLogo} className="buttonLoadingSpinner" alt="" />
            ) : (
                <>
                    {icon && (
                        <IconContext.Provider value={{ className: `buttonIcon ${children && 'buttonIcon--withText'}` }}>
                            {iconComponent}
                        </IconContext.Provider>
                    )}
                    {children}
                </>
            )}
        </>
    )

    if (link) {
        return (
            <Link
                aria-label={ariaLabel}
                className={`button button--default ${className} ${variantToStyleParser()} ${cta ? ' button--cta' : ''}${
                    !active || disabled ? ' button--disabled' : ''
                }`}
                to={to}
                onClick={e => {
                    if (active && !disabled && !loading) onClick(e)
                }}
                {...rest}
            >
                {buttonChildren}
            </Link>
        )
    }

    if (linkExternal) {
        return (
            <a
                aria-label={ariaLabel}
                className={`button button--default ${className} ${variantToStyleParser()} ${cta ? ' button--cta' : ''}${
                    !active || disabled ? ' button--disabled' : ''
                }`}
                target="_blank"
                rel="noopener noreferrer"
                href={to}
                onClick={e => {
                    if (active && !disabled && !loading) onClick(e)
                }}
                {...rest}
            >
                {buttonChildren}
            </a>
        )
    }

    return (
        <button
            aria-disabled={ariaDisabled}
            aria-label={ariaLabel}
            className={`button button--default ${className} ${variantToStyleParser()} ${cta ? ' button--cta' : ''}${
                !active || disabled ? ' button--disabled' : ''
            }`}
            disabled={!active || disabled}
            onClick={e => {
                if (active && !disabled && !loading) onClick(e)
            }}
            onKeyUp={e => {
                if (e.key === ' ' && role === 'link') e.preventDefault()
            }}
            role={role}
            type={type}
            {...rest}
        >
            {buttonChildren}
        </button>
    )
}

Button.defaultProps = {
    active: true,
    children: '',
    className: '',
    cta: false,
    disabled: false,
    onClick: () => {},
    variant: {
        condition: true,
    },
}

Button.propTypes = {
    active: PropTypes.bool,
    children: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    className: PropTypes.string,
    cta: PropTypes.bool,
    disabled: PropTypes.bool,
    onClick: PropTypes.func,
    variant: PropTypes.shape({
        name: PropTypes.string,
        condition: PropTypes.bool,
    }),
}

export default Button
